import { createSelector } from 'reselect';
import { isEmpty, isNumeric } from '../../../js/app/react/validators';
import {
  nullableGoalViewStateSelector,
  selectedGoalSelector,
} from '../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { responseDataSelector as monteCarloResponseDataSelector } from '../../MonteCarloChart/MonteCarloChartSelector';
import {
  DISABILITY,
  INSURANCE_GOAL_TYPE,
  LIFE,
  LONG_TERM_CARE,
} from '../Insurance/InsuranceConstants';
import {
  insuranceSelectedSectionSelector,
  selectedInsuranceTypeOrDefaultSelector,
} from '../Insurance/InsuranceSelector';
import {
  MONTE_CARLO,
  RETIREMENT_GOAL_TYPE,
  RETIREMENT_INCOME,
  ROTH_CONVERSION,
} from '../Retirement/RetirementConstants';
import { selectedSectionSelector as retirementSelectedSectionSelector } from '../Retirement/RetirementSelector';
import {
  ASSUMPTIONS,
  ASSUMPTIONS_TITLE,
  OVERVIEW,
  BENEFICIARIES,
  CHECKLIST,
} from './ExplanationsConst';

export const nullableGoalExplanationsSelector = createSelector(
  selectedGoalSelector,
  nullableSelectedGoal => {
    if (nullableSelectedGoal == null || isEmpty(nullableSelectedGoal.GoalExplanations)) {
      return null;
    }

    const isRetirement = nullableSelectedGoal.GoalType === RETIREMENT_GOAL_TYPE;
    const isInsurance = nullableSelectedGoal.GoalType === INSURANCE_GOAL_TYPE;
    const isEstatePlanning = nullableSelectedGoal.GoalType === 'estatePlanning';
    const goalExplanationsMap = {};

    if (isRetirement || isInsurance || isEstatePlanning) {
      if (Array.isArray(nullableSelectedGoal.GoalExplanations)) {
        if (isRetirement || isEstatePlanning) {
          nullableSelectedGoal.GoalExplanations.forEach(({ Section, ...Rest }) => {
            goalExplanationsMap[Section] = Rest;
          });
        }
        if (isInsurance) {
          nullableSelectedGoal.GoalExplanations.forEach(
            ({ Section, SubSection, OwnerName, ...Rest }) => {
              goalExplanationsMap[`${Section}.${OwnerName}.${SubSection}`] = Rest;
            },
          );
        }
        return goalExplanationsMap;
      }
      return null;
    }
    goalExplanationsMap[OVERVIEW] = nullableSelectedGoal.GoalExplanations;
    return goalExplanationsMap;
  },
);

export const nullableComposedGoalSectionSelector = createSelector(
  nullableGoalViewStateSelector,
  selectedGoalSelector,
  retirementSelectedSectionSelector,
  insuranceSelectedSectionSelector,
  (_, props) => props,
  (
    nullableGoalViewState,
    nullableSelectedGoal,
    retirementSelectedSection,
    insuranceSelectedSection,
    props,
  ) => {
    if (props.currentTab) {
      return props.currentTab;
    }

    if (!nullableSelectedGoal) {
      return null;
    }
    if (!nullableGoalViewState) {
      return null;
    }
    switch (nullableSelectedGoal.GoalType) {
      case RETIREMENT_GOAL_TYPE:
        return retirementSelectedSection;
      case INSURANCE_GOAL_TYPE:
        return insuranceSelectedSection;
      default:
        return nullableGoalViewState.selectedSection || null;
    }
  },
);

export const composedGoalSectionOrDefaultSelector = createSelector(
  nullableComposedGoalSectionSelector,
  nullableComposedGoalSection => nullableComposedGoalSection || OVERVIEW,
);

export const recommendationOrDefaultSelector = createSelector(
  selectedGoalSelector,
  monteCarloResponseDataSelector,
  nullableGoalExplanationsSelector,
  composedGoalSectionOrDefaultSelector,
  (
    nullableSelectedGoal,
    monteCarloResponseData,
    nullableGoalExplanations,
    composedGoalSectionOrDefault,
  ) => {
    const DEFAULT = {};
    if (nullableSelectedGoal == null) {
      return DEFAULT;
    }
    if (nullableSelectedGoal.GoalType === RETIREMENT_GOAL_TYPE) {
      if (composedGoalSectionOrDefault === MONTE_CARLO) {
        if (
          monteCarloResponseData &&
          isNumeric(monteCarloResponseData.MonteCarloTrialsCount) &&
          isNumeric(monteCarloResponseData.ProbabilityOfSuccess)
        ) {
          return {
            ...nullableGoalExplanations[composedGoalSectionOrDefault],
            Title: monteCarloResponseData.GoalExplanationsTitle,
            Message: monteCarloResponseData.GoalExplanationsMessage,
          };
        }
        return nullableGoalExplanations[composedGoalSectionOrDefault] || DEFAULT;
      }
    }

    if (composedGoalSectionOrDefault === ASSUMPTIONS) {
      return { Title: ASSUMPTIONS_TITLE };
    }

    if (!nullableGoalExplanations) {
      return DEFAULT;
    }

    if (!composedGoalSectionOrDefault) {
      return nullableGoalExplanations || DEFAULT;
    }

    return nullableGoalExplanations[composedGoalSectionOrDefault] || DEFAULT;
  },
);

export const recommendationMessageOrDefaultSelector = createSelector(
  recommendationOrDefaultSelector,
  recommendationOrDefault => recommendationOrDefault.Message || '\u00A0',
);

export const recommendationTitleOrDefaultSelector = createSelector(
  recommendationOrDefaultSelector,
  recommendationOrDefault => recommendationOrDefault.Title || '\u00A0',
);

export const sectionsOrDefaultSelector = createSelector(
  selectedGoalSelector,
  nullableSelectedGoal => {
    if (nullableSelectedGoal == null) {
      return [OVERVIEW];
    }
    switch (nullableSelectedGoal.GoalType) {
      case RETIREMENT_GOAL_TYPE:
        return nullableSelectedGoal.GoalExplanations.some(
          ({ Section }) => Section === ROTH_CONVERSION,
        )
          ? [OVERVIEW, RETIREMENT_INCOME, MONTE_CARLO, ROTH_CONVERSION, ASSUMPTIONS]
          : [OVERVIEW, RETIREMENT_INCOME, MONTE_CARLO, ASSUMPTIONS];
      case INSURANCE_GOAL_TYPE:
        return [LIFE, DISABILITY, LONG_TERM_CARE, ASSUMPTIONS];
      default:
        if (nullableSelectedGoal.isSubGoal) {
          return [OVERVIEW];
        }
        return [OVERVIEW, ASSUMPTIONS];
    }
  },
);

export const nullableSelectedSectionSelector = createSelector(
  nullableGoalViewStateSelector,
  selectedGoalSelector,
  retirementSelectedSectionSelector,
  selectedInsuranceTypeOrDefaultSelector,
  (
    nullableGoalViewState,
    nullableSelectedGoal,
    retirementSelectedSection,
    selectedInsuranceTypeOrDefault,
  ) => {
    if (!nullableSelectedGoal) {
      return null;
    }
    if (!nullableGoalViewState) {
      return null;
    }

    switch (nullableSelectedGoal.GoalType) {
      case RETIREMENT_GOAL_TYPE:
        return retirementSelectedSection;
      case INSURANCE_GOAL_TYPE:
        return selectedInsuranceTypeOrDefault;
      default:
        return nullableGoalViewState.selectedSection || null;
    }
  },
);

export const selectedSectionOrDefaultSelector = createSelector(
  nullableSelectedSectionSelector,
  nullableSelectedSection => nullableSelectedSection || OVERVIEW,
);

export const noTabsSelector = createSelector(
  sectionsOrDefaultSelector,
  sectionsOrDefault => sectionsOrDefault.length < 2,
);
